(function () {
  const containerSelector = '#ProductGridContainer';
  const gridSelector = '#product-grid';
  const paginationSelector = '.pagination-wrapper';
  const nextLinkSelector = '.pagination__item--prev[href]';
  const sentinelClass = 'infinite-scroll-sentinel';

  class InfiniteProductGrid {
    constructor(container) {
      this.container = container;
      this.loading = false;
      this.finished = false;
      this.observer = null;
      this.mutationObserver = null;
      this.setup();
      this.observeContainerChanges();
    }

    setup() {
      this.finished = false;
      this.observer?.disconnect();
      this.container.querySelector(`.${sentinelClass}`)?.remove();

      this.grid = this.container.querySelector(gridSelector);
      this.pagination = this.container.querySelector(paginationSelector);
      this.nextLink = this.pagination?.querySelector(nextLinkSelector);

      if (!this.grid || !this.pagination || !this.nextLink) {
        this.pagination?.removeAttribute('hidden');
        return;
      }

      this.pagination.setAttribute('hidden', '');
      this.sentinel = document.createElement('div');
      this.sentinel.className = sentinelClass;
      this.sentinel.setAttribute('aria-hidden', 'true');
      this.sentinel.style.cssText = 'display:block;width:100%;height:1px;clear:both;';
      this.container.appendChild(this.sentinel);
      this.onScroll = this.onScroll || this.checkScrollPosition.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) this.loadNextPage();
      }, { rootMargin: '900px 0px 1200px' });

      this.observer.observe(this.sentinel);
      this.checkScrollPosition();
    }

    observeContainerChanges() {
      this.mutationObserver = new MutationObserver(() => {
        const currentGrid = this.container.querySelector(gridSelector);
        if (currentGrid && currentGrid !== this.grid) this.setup();
      });
      this.mutationObserver.observe(this.container, { childList: true });
    }

    async loadNextPage() {
      if (this.loading || this.finished || !this.nextLink) return;

      this.loading = true;
      this.container.classList.add('is-loading');

      try {
        const response = await fetch(this.nextLink.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextContainer = html.querySelector(containerSelector);
        const nextGrid = nextContainer?.querySelector(gridSelector);
        const nextPagination = nextContainer?.querySelector(paginationSelector);
        const nextLink = nextPagination?.querySelector(nextLinkSelector);

        if (!nextGrid) {
          this.finish();
          return;
        }

        nextGrid.querySelectorAll(':scope > li').forEach((item) => this.grid.appendChild(item));

        if (nextPagination) {
          this.pagination.replaceWith(nextPagination);
          this.pagination = nextPagination;
          this.pagination.setAttribute('hidden', '');
        }

        this.nextLink = nextLink || null;
        if (!this.nextLink) this.finish();
      } catch (error) {
        console.error('Infinite scroll failed:', error);
        this.pagination?.removeAttribute('hidden');
        this.finish();
      } finally {
        this.loading = false;
        this.container.classList.remove('is-loading');
      }
    }

    finish() {
      this.finished = true;
      this.observer?.disconnect();
      this.sentinel?.remove();
      this.sentinel = null;
      this.nextLink = null;
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }

    checkScrollPosition() {
      if (this.loading || this.finished || !this.nextLink || !this.sentinel) return;

      const sentinelTop = this.sentinel.getBoundingClientRect().top;
      if (sentinelTop < window.innerHeight + 1200) this.loadNextPage();
    }
  }

  function initInfiniteScroll() {
    document.querySelectorAll(containerSelector).forEach((container) => {
      container.querySelector(`.${sentinelClass}`)?.remove();
      container.dataset.infiniteScrollInitialized = 'true';
      new InfiniteProductGrid(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }
})();
